import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, DollarSign, TrendingUp, Repeat, ExternalLink, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { detectCurrency } from "../lib/money";
import { openPrivateFile } from "../lib/privatePhotos";
import {
  findPayoutInvoice,
  isImportedPayout,
  PAYOUT_ENTITY_REQUESTS,
  payoutRowsFromEntities,
  uploadPayoutInvoice,
} from "./money/payoutRecords";

const LEDGER_TYPES = { live_show: "Live Shows", studio_payout: "Studios" };
const PLATFORM_METRIC_FIELDS = [
  { id: "balance", label: "Current balance", pattern: /(?:onlyfans_)?current_balance|available_balance|faphouse_balance|^balance$/ },
  { id: "pending", label: "Pending balance", pattern: /(?:onlyfans_)?pending_balance|^pending$/ },
  { id: "net", label: "Net earnings", pattern: /earnings_net_total|net_earnings|net_earning/ },
  { id: "paid", label: "Total paid / withdrawn", pattern: /total_paid|paid_amount|total_withdrawn|withdrawn_amount/ },
];
const REVENUE_TYPES = ["tip", "ppv", "subscription", "custom", "other"];
const REVENUE_SOURCES = ["manual", "extension", "import", "system"];
const SOURCE_REVIEW_PAGE_SIZE = 100;
const PLATFORMS = ["onlyfans_vip", "onlyfans_free", "justforfans", "faphouse", "rawfuckclub", "twitter", "instagram", "tiktok", "bluesky", "paypal", "cashapp", "other"];
const PLATFORM_LABELS = {
  onlyfans_vip: "OnlyFans VIP",
  onlyfans_free: "OnlyFans Free",
  onlyfans: "OnlyFans",
  justforfans: "JustForFans",
  faphouse: "Faphouse",
  theblowers: "TheBlowers",
  rawfuckclub: "RawFuckClub",
  live_show: "Live Shows",
};

function formatCurrencyCodes(totals) {
  const entries = Object.entries(totals || {}).sort(([left], [right]) => left.localeCompare(right));
  return entries.length
    ? entries.map(([currency, amount]) => `${currency} ${Number(amount).toFixed(2)}`).join(" · ")
    : "No entries";
}

function formatRecordAmount(amount, currency) {
  if (amount === null || amount === undefined || amount === "" || !Number.isFinite(Number(amount))) return "—";
  return `${String(currency || "Unknown currency").toUpperCase()} ${Number(amount).toFixed(2)}`;
}

function ledgerType(entry) {
  const type = String(entry.revenue_type || "").toLowerCase();
  return Object.prototype.hasOwnProperty.call(LEDGER_TYPES, type) ? type : null;
}

function latestExtensionRevenueObservations(metrics, syncRuns) {
  const runsById = new Map((syncRuns || []).map((run) => [run.sync_run_id, run]));
  const latest = new Map();
  for (const metric of metrics || []) {
    const run = runsById.get(metric.sync_run_id);
    if (!run || run.status !== "completed" || run.platform !== metric.platform
      || run.account_id !== metric.account_id || (Array.isArray(run.errors) && run.errors.length > 0)
      || !Array.isArray(run.source_pages) || !run.source_pages.some((page) => /^https:\/\//i.test(String(page || "")))) continue;
    const origins = [metric.source, metric.origin, metric.collector, metric.capture_source,
      run.source, run.origin, run.collector, run.capture_source]
      .filter((value) => value != null && String(value).trim());
    if (origins.some((value) => !["extension", "browser_extension", "extension_sync"].includes(String(value).trim().toLowerCase()))) continue;
    const key = [metric.platform, metric.account_id, metric.metric_key || metric.normalized_key || metric.original_label].join("\u0000");
    const current = latest.get(key);
    if (!current || String(metric.captured_at || "") > String(current.captured_at || "")) latest.set(key, metric);
  }

  return Array.from(latest.values()).flatMap((metric) => {
    const identifiers = [metric.metric_key, metric.normalized_key, metric.original_label]
      .filter(Boolean).map((value) => String(value).toLowerCase().replace(/[\s-]+/g, "_"));
    if (identifiers.some((identifier) => /total_revenue|platform_stats|gross_earnings/.test(identifier))) return [];
    const field = PLATFORM_METRIC_FIELDS.find((candidate) => identifiers.some((identifier) => candidate.pattern.test(identifier)));
    if (!field || (!metric.currency && metric.data_type !== "currency")
      || metric.parsed_value === null || metric.parsed_value === undefined || metric.parsed_value === ""
      || !Number.isFinite(Number(metric.parsed_value))) return [];
    return [{ ...metric, revenue_field: field.id, revenue_label: field.label }];
  }).sort((left, right) => String(right.captured_at || "").localeCompare(String(left.captured_at || "")));
}

function totalsByCurrency(rows) {
  return (rows || []).reduce((totals, row) => {
    const amount = Number(row.amount);
    if (!Number.isFinite(amount)) return totals;
    const currency = String(row.currency || "USD").toUpperCase();
    totals[currency] = Math.round(((totals[currency] || 0) + amount) * 100) / 100;
    return totals;
  }, {});
}

async function loadAllRevenueEntries() {
  const rows = [];
  const limit = 1000;
  while (true) {
    const response = await api.get("/api/revenue", { params: { skip: rows.length, limit } });
    const batch = response.data || [];
    rows.push(...batch);
    if (batch.length < limit) return rows;
  }
}

export default function Revenue({ extraTabBar }) {
  const [entries, setEntries] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [extensionMetrics, setExtensionMetrics] = useState([]);
  const [extensionSyncRuns, setExtensionSyncRuns] = useState([]);
  const [extensionEntities, setExtensionEntities] = useState({});
  const [legalReceipts, setLegalReceipts] = useState([]);
  const [invoiceUploadId, setInvoiceUploadId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sourceReviewOpen, setSourceReviewOpen] = useState(false);
  const [sourceReviewRows, setSourceReviewRows] = useState([]);
  const [sourceReviewTotal, setSourceReviewTotal] = useState(0);
  const [sourceReviewSkip, setSourceReviewSkip] = useState(0);
  const [classifyingId, setClassifyingId] = useState(null);
  const extensionObservations = latestExtensionRevenueObservations(extensionMetrics, extensionSyncRuns);
  const platformRowsByAccount = new Map();
  extensionObservations.forEach((metric) => {
    const key = `${metric.platform}\u0000${metric.account_id || ""}`;
    const row = platformRowsByAccount.get(key) || {
      id: key,
      platform: metric.platform,
      account_id: metric.account_id,
      captured_at: metric.captured_at,
      metrics: {},
    };
    row.metrics[metric.revenue_field] = metric;
    if (String(metric.captured_at || "") > String(row.captured_at || "")) row.captured_at = metric.captured_at;
    platformRowsByAccount.set(key, row);
  });
  const platformRows = Array.from(platformRowsByAccount.values());
  const extensionPayoutRows = payoutRowsFromEntities(extensionEntities);
  const liveShowRows = ledgerEntries.filter((entry) => ledgerType(entry) === "live_show");
  const studioRows = ledgerEntries.filter((entry) => ledgerType(entry) === "studio_payout");
  const platformNetTotals = totalsByCurrency(platformRows.flatMap((row) => row.metrics.net ? [{
    amount: row.metrics.net.parsed_value,
    currency: row.metrics.net.currency || detectCurrency(undefined, row.metrics.net.raw_value).currency,
  }] : []));
  const liveShowTotals = totalsByCurrency(liveShowRows);
  const studioTotals = totalsByCurrency(studioRows);

  function empty() {
    return {
      amount: "", currency: "USD", source: "manual", revenue_type: "tip", platform: "",
      fan_handle: "", fan_name: "", linked_collab_id: "", linked_conversation_note: "",
      recurring: false, date: new Date().toISOString().slice(0, 10), notes: "",
    };
  }

  const load = async (selectedSource = sourceFilter) => {
    const [all, c, extension, syncRuns, ...payoutResponses] = await Promise.all([
      loadAllRevenueEntries(),
      api.get("/api/collabs").catch(() => ({ data: [] })),
      api.get("/api/platform-data/metrics", { params: { limit: 5000 } }).catch(() => ({ data: { metrics: [] } })),
      api.get("/api/platform-data/sync-runs", { params: { limit: 500 } }).catch(() => ({ data: { sync_runs: [] } })),
      ...PAYOUT_ENTITY_REQUESTS.map((request) => api.get("/api/platform-data/entities", { params: { ...request, limit: 1000 } }).catch(() => ({ data: { entities: [] } }))),
      api.get("/api/legal/", { params: { doc_type: "receipt" } }).catch(() => ({ data: [] })),
    ]);
    const eligible = all.filter((entry) => ledgerType(entry) || isImportedPayout(entry)
      || ["theblowers", "payout"].includes(String(entry.revenue_type || "").toLowerCase()));
    const entityResponses = payoutResponses.slice(0, PAYOUT_ENTITY_REQUESTS.length);
    const receiptsResponse = payoutResponses.at(-1);
    setLedgerEntries(eligible);
    setEntries(eligible.filter((entry) => selectedSource === "all"
      || (selectedSource === "unlabeled" ? !entry.source : entry.source === selectedSource)));
    setCollabs(c.data);
    setExtensionMetrics(extension.data.metrics || []);
    setExtensionSyncRuns(syncRuns.data.sync_runs || []);
    setExtensionEntities(Object.fromEntries(PAYOUT_ENTITY_REQUESTS.map((request, index) => [
      `${request.platform}:${request.entity_type}`,
      entityResponses[index]?.data?.entities || [],
    ])));
    setLegalReceipts(receiptsResponse?.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty(), ...it, amount: String(it.amount), linked_collab_id: it.linked_collab_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.amount || !form.date) return toast.error("Amount and date required");
    const payload = { ...form, source: editing ? undefined : "manual", amount: parseFloat(form.amount), linked_collab_id: form.linked_collab_id || null };
    try {
      if (editing) await api.put(`/api/revenue/${editing.id}`, payload);
      else await api.post("/api/revenue", payload);
      setOpen(false); load();
      toast.success(editing ? "Updated" : "Logged 💰");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/api/revenue/${id}`); load(); };

  const loadSourceReview = async (skip = sourceReviewSkip) => {
    try {
      const response = await api.get("/api/revenue/source-review", { params: { skip, limit: SOURCE_REVIEW_PAGE_SIZE } });
      setSourceReviewRows(response.data.items || []);
      setSourceReviewTotal(response.data.total || 0);
      setSourceReviewSkip(skip);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load unlabeled revenue rows");
    }
  };

  const classifySource = async (row, source) => {
    if (!REVENUE_SOURCES.includes(source) || classifyingId) return;
    setClassifyingId(row.id);
    try {
      await api.post(`/api/revenue/${row.id}/source`, { source });
      await Promise.all([loadSourceReview(sourceReviewSkip), load(sourceFilter)]);
      toast.success("Source classification saved and audited");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not classify this entry");
    } finally {
      setClassifyingId(null);
    }
  };

  const handleInvoiceUpload = async (row, file) => {
    if (!file) return;
    setInvoiceUploadId(row.attachmentKey);
    try {
      await uploadPayoutInvoice(api, row, file);
      const { data } = await api.get("/api/legal/", { params: { doc_type: "receipt" } });
      setLegalReceipts(data || []);
      toast.success("Invoice saved to Legal receipts");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || "Could not upload invoice");
    } finally {
      setInvoiceUploadId(null);
    }
  };

  const openInvoice = (invoice) => {
    if (invoice.file_asset?.public_id) {
      openPrivateFile("legal", invoice.id, "file").catch(() => toast.error("Could not open invoice"));
    } else if (invoice.file_url) {
      window.open(invoice.file_url, "_blank", "noopener,noreferrer");
    }
  };

  const renderInvoiceAction = (row) => {
    const invoice = findPayoutInvoice(legalReceipts, row);
    if (invoice) return <button type="button" onClick={() => openInvoice(invoice)} className="inline-flex min-h-9 items-center gap-1.5 text-xs text-emerald-300 hover:underline" data-testid={`rev-invoice-${row.attachmentKey}`} aria-label={`Open invoice for ${row.platformLabel || PLATFORM_LABELS[row.platform] || row.platform}`}>
      <FileText size={13} /> Invoice <ExternalLink size={12} />
    </button>;
    return <label data-testid={`rev-invoice-upload-${row.attachmentKey}`} className="inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-300 hover:border-zinc-500 focus-within:border-[color:var(--gio-primary)]">
      <input type="file" accept="application/pdf,image/*" className="sr-only" disabled={invoiceUploadId === row.attachmentKey} aria-label={`Upload invoice for ${row.platformLabel || PLATFORM_LABELS[row.platform] || row.platform}`} onChange={(event) => { handleInvoiceUpload(row, event.target.files?.[0]); event.target.value = ""; }} />
      <Upload size={13} /> {invoiceUploadId === row.attachmentKey ? "Uploading…" : "Upload invoice"}
    </label>;
  };

  const visibleRecordCount = platformRows.length + extensionPayoutRows.length + entries.length;
  return (
    <AppLayout title="Revenue" overline="PLATFORMS · LIVE SHOWS · STUDIOS">
      {extraTabBar}
      <section className="mb-6 border-y border-zinc-800 py-4" data-testid="revenue-source-note">
        <p className="text-xs leading-5 text-zinc-400">One register combines latest platform observations, captured payouts, imported payouts, and app ledger entries. Each row keeps its source and currency; account snapshots are not counted as individual payouts.</p>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-8 fade-up" data-testid="revenue-summary">
        <Summary label="Platform net earnings" value={formatCurrencyCodes(platformNetTotals)} icon={<DollarSign size={16} />} testid="rev-platform-net" source="Extension" big />
        <Summary label="Live Shows" value={formatCurrencyCodes(liveShowTotals)} icon={<TrendingUp size={16} />} testid="rev-live-show" source="Ledger" />
        <Summary label="Studios" value={formatCurrencyCodes(studioTotals)} icon={<Repeat size={16} />} testid="rev-studio" source="Ledger" />
        <Summary label="Visible records" value={String(visibleRecordCount)} testid="rev-count" source="Extension + Ledger" />
      </section>

      <div className="flex items-center justify-between mb-4">
        <div className="overline">ALL ENTRIES</div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-zinc-400">Source
            <select data-testid="rev-source-filter" value={sourceFilter} onChange={(event) => {
              const selected = event.target.value;
              setSourceFilter(selected);
              load(selected);
            }} className="gio-input w-auto min-w-32">
              <option value="all">All</option>
              {REVENUE_SOURCES.map((source) => <option key={source} value={source}>{source[0].toUpperCase() + source.slice(1)}</option>)}
              <option value="unlabeled">Unlabeled</option>
            </select>
          </label>
          <button type="button" onClick={async () => {
            const opening = !sourceReviewOpen;
            setSourceReviewOpen(opening);
            if (opening) await loadSourceReview(0);
          }} className="btn-ghost" data-testid="rev-source-review-toggle">Review unlabeled</button>
          <button onClick={openNew} className="btn-primary inline-flex items-center gap-2" data-testid="rev-new-button">
            <Plus size={16} /> Log payment
          </button>
        </div>
      </div>

      {sourceReviewOpen && <section className="mb-6 border border-zinc-800 bg-zinc-950 p-4" data-testid="rev-source-review">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div><h2 className="text-sm font-semibold text-white">Legacy source review</h2><p className="mt-1 text-xs text-zinc-500">Review each row individually. Each choice records your user ID and timestamp; no rows are changed until you click a label.</p></div>
          <span className="whitespace-nowrap text-xs text-zinc-500">Showing {sourceReviewTotal ? sourceReviewSkip + 1 : 0}–{Math.min(sourceReviewSkip + sourceReviewRows.length, sourceReviewTotal)} of {sourceReviewTotal}</span>
        </div>
        {sourceReviewRows.length === 0 ? <p className="py-4 text-sm text-zinc-600">No unlabeled rows.</p> : <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] text-sm"><thead><tr className="border-b border-zinc-800 text-left"><th className="p-2">Date</th><th className="p-2 text-right">Amount</th><th className="p-2">Currency</th><th className="p-2">Revenue type</th><th className="p-2">Data store</th><th className="p-2">Current source</th><th className="p-2">Created</th><th className="p-2">Source URL / ref</th><th className="p-2">Actions</th></tr></thead>
            <tbody>{sourceReviewRows.map((row) => <tr key={row.id} className="border-b border-zinc-900" data-testid={`rev-review-row-${row.id}`}>
              <td className="p-2 text-zinc-400">{row.date || "—"}</td>
              <td className="p-2 text-right">{Number(row.amount || 0).toFixed(2)}</td>
              <td className="p-2 text-zinc-400">{row.currency || "—"}</td>
              <td className="p-2 text-zinc-400">{row.revenue_type || "Unknown"}</td>
              <td className="p-2"><SourceBadge source="Ledger" /></td>
              <td className="p-2 text-zinc-400">{row.current_source || row.source || "Unknown"}</td>
              <td className="p-2 text-zinc-500">{row.created_at || "—"}</td>
              <td className="max-w-56 break-all p-2 text-xs text-zinc-400">{row.source_url && /^https?:\/\//i.test(row.source_url)
                ? <a href={row.source_url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">{row.source_url}</a>
                : row.source_ref || row.reference || row.external_id || row.invoice_number || row.import_id || "—"}</td>
              <td className="p-2"><div className="flex flex-wrap gap-1">
                {REVENUE_SOURCES.map((source) => <button key={source} type="button" disabled={classifyingId !== null} onClick={() => classifySource(row, source)} className="btn-ghost whitespace-nowrap px-2 py-1 text-[11px] disabled:opacity-40" data-testid={`rev-source-${source}-${row.id}`}>Mark as {source[0].toUpperCase() + source.slice(1)}</button>)}
              </div></td>
            </tr>)}</tbody>
          </table>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" disabled={sourceReviewSkip === 0} onClick={() => loadSourceReview(Math.max(0, sourceReviewSkip - SOURCE_REVIEW_PAGE_SIZE))} className="btn-ghost disabled:opacity-40" data-testid="rev-source-review-prev">Previous</button>
            <button type="button" disabled={sourceReviewSkip + sourceReviewRows.length >= sourceReviewTotal} onClick={() => loadSourceReview(sourceReviewSkip + SOURCE_REVIEW_PAGE_SIZE)} className="btn-ghost disabled:opacity-40" data-testid="rev-source-review-next">Next</button>
          </div>
        </div>}
      </section>}

      <section className="border border-zinc-900 bg-zinc-950 overflow-hidden" data-testid="revenue-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="border-b border-zinc-900 text-left">
                <th className="p-3 overline">Revenue</th>
                <th className="p-3 overline">Source</th>
                <th className="p-3 overline">Platform / account</th>
                <th className="p-3 overline">Captured / date</th>
                <th className="p-3 overline text-right">Balance</th>
                <th className="p-3 overline text-right">Pending</th>
                <th className="p-3 overline text-right">Net / amount</th>
                <th className="p-3 overline text-right">Paid / withdrawn</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {platformRows.map((row) => (
                <tr key={`extension:${row.id}`} className="border-b border-zinc-900/50 hover:bg-zinc-900/30" data-testid={`rev-platform-${row.platform}-${row.account_id || "account"}`}>
                  <td className="p-3 text-zinc-200">Platform earnings</td>
                  <td className="p-3"><SourceBadge source="Extension" /></td>
                  <td className="p-3 text-zinc-300">{PLATFORM_LABELS[row.platform] || row.platform} · {row.account_id || "Account"}</td>
                  <td className="p-3 text-zinc-400 font-mono-ui text-xs">{row.captured_at ? new Date(row.captured_at).toLocaleString() : "Unavailable"}</td>
                  {PLATFORM_METRIC_FIELDS.map((field) => {
                    const metric = row.metrics[field.id];
                    const value = metric
                      ? `${String(metric.currency || detectCurrency(undefined, metric.raw_value).currency).toUpperCase()} ${Number(metric.parsed_value).toFixed(2)}`
                      : "—";
                    return <td key={field.id} className="p-3 text-right font-mono-ui text-white" data-testid={`rev-platform-${field.id}`}>{value}</td>;
                  })}
                  <td className="p-3" />
                </tr>
              ))}
              {extensionPayoutRows.map((row) => {
                const tableRow = { ...row, platformLabel: PLATFORM_LABELS[row.platform] || row.platform };
                return <tr key={row.id} className="border-b border-zinc-900/50 hover:bg-zinc-900/30" data-testid={`rev-payout-${row.id}`}>
                  <td className="p-3 text-zinc-200"><div>{row.kind}</div><div className="mt-1 text-xs text-zinc-500">{row.status}</div>{row.notes && <div className="mt-1 text-xs text-zinc-500">{row.notes}</div>}</td>
                  <td className="p-3"><SourceBadge source="Extension" /></td>
                  <td className="p-3 text-zinc-300">{tableRow.platformLabel}{row.accountId ? ` · ${row.accountId}` : ""}</td>
                  <td className="p-3 text-zinc-400 font-mono-ui text-xs">{row.date || "Unavailable"}</td>
                  <td className="p-3 text-right text-zinc-600">—</td>
                  <td className="p-3 text-right text-zinc-600">—</td>
                  <td className="p-3 text-right font-mono-ui text-white">{formatRecordAmount(row.amount, row.currency)}</td>
                  <td className="p-3 text-right text-zinc-600">—</td>
                  <td className="p-3">{renderInvoiceAction(tableRow)}</td>
                </tr>;
              })}
              {entries.map((entry) => {
                const type = ledgerType(entry);
                const linkedCollab = collabs.find((collab) => collab.id === entry.linked_collab_id);
                const source = entry.source ? entry.source[0].toUpperCase() + entry.source.slice(1) : "Ledger";
                const entryType = type ? LEDGER_TYPES[type]
                  : String(entry.revenue_type || "").toLowerCase() === "theblowers" ? "TheBlowers payout" : "Platform payout";
                const tableRow = {
                  attachmentKey: entry.id,
                  legalRevenueId: entry.id,
                  platform: entry.platform || "other",
                  platformLabel: PLATFORM_LABELS[entry.platform] || entry.platform || entryType,
                  kind: entryType,
                  source,
                  reference: entry.invoice_number || entry.reference || "",
                  date: entry.payment_date || entry.date || "",
                  amount: entry.amount,
                  currency: entry.currency,
                  notes: entry.notes || "",
                };
                return (
                  <tr key={`ledger:${entry.id}`} className="border-b border-zinc-900/50 group hover:bg-zinc-900/30" data-testid={`rev-row-${entry.id}`}>
                    <td className="p-3 text-zinc-200">
                      <div>{entryType}</div>
                      {entry.notes && <div className="mt-1 text-xs text-zinc-500">{entry.notes}</div>}
                      {linkedCollab && <div className="overline mt-1 text-[color:var(--gio-secondary)]">→ {linkedCollab.partner_name}</div>}
                    </td>
                    <td className="p-3"><SourceBadge source={source} /></td>
                    <td className="p-3 text-zinc-300">{tableRow.platformLabel}{entry.of_account ? ` · ${entry.of_account}` : ""}</td>
                    <td className="p-3 text-zinc-400 font-mono-ui text-xs">{tableRow.date || "—"}</td>
                    <td className="p-3 text-right text-zinc-600">—</td>
                    <td className="p-3 text-right text-zinc-600">—</td>
                    <td className="p-3 text-right font-mono-ui text-[color:var(--gio-secondary)]">{String(entry.currency || "USD").toUpperCase()} {Number(entry.amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-right text-zinc-600">—</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {renderInvoiceAction(tableRow)}
                        {type && <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                          <button onClick={() => openEdit(entry)} className="min-h-9 min-w-9 text-zinc-500 hover:text-[color:var(--gio-primary)]" data-testid={`rev-edit-${entry.id}`} aria-label="Edit revenue entry"><Pencil size={14} /></button>
                          <button onClick={() => remove(entry.id)} className="min-h-9 min-w-9 text-zinc-500 hover:text-[color:var(--gio-primary)]" data-testid={`rev-delete-${entry.id}`} aria-label="Delete revenue entry"><Trash2 size={14} /></button>
                        </div>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {platformRows.length === 0 && extensionPayoutRows.length === 0 && entries.length === 0 && (
                <tr><td colSpan={9} className="p-12 text-center text-zinc-600">No platform observations or live-show / studio ledger entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="rev-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6 my-8">
            <div className="overline mb-4 text-[color:var(--gio-primary)]">{editing ? "EDIT" : "LOG"} · PAYMENT</div>
            <div className="grid grid-cols-2 gap-3">
              <F l="Amount"><input data-testid="rev-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="gio-input" /></F>
              <F l="Currency"><input data-testid="rev-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="gio-input" /></F>
              <F l="Revenue type">
                <select data-testid="rev-revenue-type" value={form.revenue_type} onChange={(e) => setForm({ ...form, revenue_type: e.target.value })} className="gio-input">
                  {REVENUE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <F l="Platform">
                <select data-testid="rev-platform" value={form.platform} required onChange={(e) => setForm({ ...form, platform: e.target.value })} className="gio-input">
                  <option value="" disabled>Select platform</option>
                  {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABELS[p] || p}</option>)}
                </select>
              </F>
              <F l="Fan handle"><input data-testid="rev-handle" value={form.fan_handle} onChange={(e) => setForm({ ...form, fan_handle: e.target.value })} placeholder="@username" className="gio-input" /></F>
              <F l="Fan name"><input data-testid="rev-name" value={form.fan_name} onChange={(e) => setForm({ ...form, fan_name: e.target.value })} className="gio-input" /></F>
              <F l="Date"><input data-testid="rev-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="gio-input" /></F>
              <F l="Recurring">
                <label className="flex items-center gap-2 h-[38px]">
                  <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} data-testid="rev-recurring" className="accent-[color:var(--gio-primary)]" />
                  <span className="text-zinc-400 font-mono-ui text-xs uppercase">Monthly sub</span>
                </label>
              </F>
            </div>
            <F l="Linked collab (optional)">
              <select data-testid="rev-collab" value={form.linked_collab_id} onChange={(e) => setForm({ ...form, linked_collab_id: e.target.value })} className="gio-input">
                <option value="">—</option>
                {collabs.map((c) => <option key={c.id} value={c.id}>{c.partner_name}</option>)}
              </select>
            </F>
            <F l="Notes"><textarea data-testid="rev-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="gio-input" /></F>
            <div className="flex gap-3 mt-4">
              <button onClick={save} className="btn-primary flex-1" data-testid="rev-save-button">Save</button>
              <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="rev-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.gio-input { width: 100%; background: #000; border: 1px solid #27272a; color: white; padding: 10px; font-size: 13px; outline: none; } .gio-input:focus { border-color: var(--gio-primary); }`}</style>
    </AppLayout>
  );
}

function F({ l, children }) { return <div className="mb-3"><div className="overline mb-2">{l}</div>{children}</div>; }

function SourceBadge({ source }) {
  return <span className="inline-flex rounded border border-zinc-800 px-1.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-wider text-zinc-400" data-testid={`revenue-source-${source.toLowerCase().replace(/\s+/g, "-")}`}>{source}</span>;
}

function Summary({ label, value, icon, testid, source, big }) {
  return (
    <div className={`border border-zinc-900 bg-zinc-950 p-5 ${big ? "lg:col-span-1" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 overline mb-3">{icon}{label}</div>
      <div className="break-words font-heading text-white leading-tight text-2xl">{value}</div>
      <div className="mt-3"><SourceBadge source={source} /></div>
    </div>
  );
}
